import {
  ActiveOrder,
  MapCenter,
  MapCoordinate,
  MapCoordinates,
} from '@/resources/types'
import emotionTheme from '@/styles/theme'
import {
  getScheduledColorTextByTime,
  getTimeColor,
  scheduledTimeColors,
} from '@/resources/utils'
import { getCardColorByType } from '@/pages/Orders/utils'
import { DeliveryType } from '@/resources/enums/deliveryTypes'
import iconReceiveFilter from '@/assets/imgs/icon-receive-gray.svg'
import iconScheduledFilter from '@/assets/imgs/icon-scheduled.svg'
import iconTurboFilter from '@/assets/imgs/icon-turbo.svg'
import iconPinPoc from '@/assets/imgs/map-pin-poc.svg'
import { Location } from '@/resources/types/seller'
import { OrderStatus } from '@/resources/enums'
import { ORDERS_MAP as dict } from '@/resources/dict'
import {
  OrderColors,
  OrderColorHex,
  TurboColor,
} from '@/resources/enums/orders'

const isGoogleMapLoaded = () => 'google' in window && !!google?.maps

export interface ObtainCustomizedMapProps {
  mapDiv: HTMLElement
  mapId: string
  lat: number
  lng: number
  scrollwheel?: boolean
}

export interface MapFilterOption {
  label: string
  value: string
}

export interface MapSelectedOptions {
  [value: string]: boolean
}

export interface MapFilterProps {
  options: MapFilterOption[]
  checkedOptions: MapSelectedOptions
  onFiltersChange(option: string, value: boolean): void
}

export type MapPinCallback = (order: ActiveOrder, color?: OrderColors) => void

export const filterLabelByOption: { [key: string]: string } = {
  [DeliveryType.RECEIVE]: dict.filters.receive,
  [DeliveryType.SCHEDULED]: dict.filters.scheduled,
  [DeliveryType.TURBO]: dict.filters.turbo,
}

export const getMapFilterOptions = (
  hasTurbo: boolean,
  hasScheduled: boolean
): MapFilterOption[] => {
  const types = [DeliveryType.RECEIVE]
  if (hasTurbo) types.push(DeliveryType.TURBO)
  if (hasScheduled) types.push(DeliveryType.SCHEDULED)

  return types.map(type => ({
    label: filterLabelByOption[type],
    value: type,
  }))
}

export const mapFilterIcons: { [type: string]: string } = {
  [DeliveryType.RECEIVE]: iconReceiveFilter,
  [DeliveryType.SCHEDULED]: iconScheduledFilter,
  [DeliveryType.TURBO]: iconTurboFilter,
}

export const obtainCustomizedMap = ({
  mapDiv,
  mapId,
  lat,
  lng,
  scrollwheel = false,
}: ObtainCustomizedMapProps) => {
  if (isGoogleMapLoaded()) {
    return new google.maps.Map(mapDiv, {
      center: { lat, lng },
      zoom: 13,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: true,
      scrollwheel,
      mapId,
    })
  }

  return null
}

export interface ObtainCustomMarkerProps {
  lat: number
  lng: number
  map: google.maps.Map | null
  icon: { url: string }
  title?: string
}

export const obtainCustomMarker = ({
  lat,
  lng,
  map,
  icon,
  title = 'marker',
}: ObtainCustomMarkerProps) => {
  if (isGoogleMapLoaded()) {
    const position = new google.maps.LatLng(lat, lng)
    const iconMarker = document.createElement('img')
    iconMarker.src = icon.url

    return new google.maps.marker.AdvancedMarkerElement({
      position,
      map,
      content: iconMarker,
      title,
    })
  }

  return null
}

export interface MountPolygonsProps {
  coordinates: Array<Array<MapCoordinates>>
}

export const mountPolygons = (
  { coordinates }: MountPolygonsProps,
  map: google.maps.Map
) => {
  if (isGoogleMapLoaded()) {
    coordinates.forEach((coordinate = []) => {
      const pointArr =
        coordinate?.flatMap(geoCoordinate => geoCoordinate) ?? coordinate[0]
      const pol = new google.maps.Polygon({
        paths: pointArr.map(point => ({ lat: point[1], lng: point[0] })),
        strokeColor: emotionTheme.ds.ColorBaseBlack,
        strokeOpacity: 0.3,
        strokeWeight: 3,
        fillColor: '#fcd500',
        fillOpacity: 0.18,
      })
      pol.setMap(map)
    })
  }
}

export const obtainMapCenterFromMapCoordinate = (
  coordinates: MapCoordinate
): MapCenter | null => {
  try {
    const mapCenter: MapCenter = {
      lat: coordinates?.[1],
      lng: coordinates?.[0],
    }
    const isValidCoordinates =
      typeof mapCenter.lat === 'number' && typeof mapCenter.lng === 'number'

    if (!isValidCoordinates)
      throw new Error(
        `Invalid coordinates!\nResult: ${JSON.stringify(mapCenter)}`
      )
    return mapCenter
  } catch (error) {
    console.error(error)
    return null
  }
}

export const getOrderMarkerColor = ({
  status,
  time,
  deliveryType,
  deliveryDate,
}: {
  status: OrderStatus
  time: number
  deliveryType: string
  deliveryDate?: Date
}): string => {
  const minutes = time / 60
  let color = getTimeColor(status, minutes, deliveryType === DeliveryType.TURBO)

  if (DeliveryType.SCHEDULED === deliveryType && deliveryDate) {
    color =
      getScheduledColorTextByTime(deliveryDate) ??
      scheduledTimeColors.GREEN.text
  }

  return color
}

export const getOrderMarkerPinSvgDataUri = (svgString: string) => {
  const encoded = encodeURIComponent(svgString)
  return `data:image/svg+xml,${encoded}`
}

export const getMap = (mapId: string, pocCoordinates?: Location) => {
  const configMapId = process.env.GOOGLE_MAPS_ID ?? mapId

  return obtainCustomizedMap({
    mapDiv: document.getElementById(mapId) || ({} as HTMLElement),
    mapId: configMapId,
    lat: pocCoordinates?.coordinates[1] ?? 0,
    lng: pocCoordinates?.coordinates[0] ?? 0,
    scrollwheel: true,
  })
}

export const getPocMarker = (map: google.maps.Map, pocCoordinates: Location) =>
  obtainCustomMarker({
    lat: pocCoordinates.coordinates[1],
    lng: pocCoordinates.coordinates[0],
    map,
    icon: {
      url: iconPinPoc,
    },
  })

export const setMapBounds = (
  map: google.maps.Map,
  markers: Array<google.maps.marker.AdvancedMarkerElement | null>
) => {
  const bounds = new google.maps.LatLngBounds()

  markers.forEach(marker => {
    const position = marker?.position

    if (position) bounds.extend(position)
  })

  map.fitBounds(bounds)
}

export const getColorNameByHex = (colorHex: OrderColorHex) => {
  switch (colorHex) {
    case OrderColorHex.GREEN:
      return OrderColors.GREEN
    case OrderColorHex.YELLOW:
      return OrderColors.YELLOW
    case OrderColorHex.RED:
    default:
      return OrderColors.RED
  }
}

export const addColorBorderToMapTooltip = (
  orderNumber: string,
  deliveryType: DeliveryType,
  turboColor?: TurboColor
) => {
  const tooltipCards = document.querySelectorAll('.gm-style-iw-c') ?? []
  const card = Array.from(tooltipCards).find(element =>
    element.querySelector(`#tooltip-order-${orderNumber}`)
  ) as HTMLElement
  if (card) {
    card.style.boxShadow = `inset 5px 0px ${getCardColorByType(deliveryType, turboColor)}`
  }
}

export const getMapOffsetForTooltipSize = (productsLength: number) => {
  const defaultOffset = -45
  const productsSizeOffset = 10
  const size = productsLength >= 4 ? 4 : productsLength
  return defaultOffset - size * productsSizeOffset
}
